import React from 'react';

import { ApolloMiddleware, Error } from '@nxte-nl/apollo-hooks';

import { GlobalMessages } from '../../types/Messages';
import { ErrorPresentational } from '../../types/common';
import { errorHooksIntl, UseErrorMessagesIntlParams } from '../errorHooksIntl/errorHooksIntl';
import { featureStateHooks } from '../featureStateHooks/featureStateHooks';
import { messageHooks } from '../messageHooks/messageHooks';

export const apolloMiddlewareHooks = {
  useTranslateError(apiErrorMessage: ErrorPresentational | null,
    messagesParams: UseErrorMessagesIntlParams) {
    const { translateErrorCallback } = errorHooksIntl.useTranslateErrorCallback(messagesParams);

    const apiTranslatedError =
    React.useMemo(() => {
      const translatedError = translateErrorCallback(apiErrorMessage);
      if (translatedError?.intlMessageFormatted) {
        const { intlMessageFormatted } = translatedError;
        return intlMessageFormatted;
      } if (apiErrorMessage?.message) {
        return apiErrorMessage.message;
      }
      return null;
    }, [ translateErrorCallback, apiErrorMessage]);
    return { apiTranslatedError };
  },

  useGetErrorMessage(apiTranslatedErrorMessage: string | null,
    errorMessage: string | null) {
    const { isErrorMessages } = featureStateHooks.useFeatureToggles();

    const chosenErrorMessage = isErrorMessages ? apiTranslatedErrorMessage : errorMessage;

    return chosenErrorMessage;
  },

  useDefaultErrorMiddleware(): ApolloMiddleware {
    const setGlobalMessagesAPIErrorModal = messageHooks.useNext(GlobalMessages.APIErrorModal);

    return React.useCallback((response) => {
      const contextErrors = response.errors;
      const responseData = response.data?.data;

      const dataErrors = typeof responseData === 'object'
        && responseData !== null
        && 'errors' in responseData
        && responseData.errors;

      if (dataErrors && dataErrors.length) {
        const error = dataErrors?.[0];
        setGlobalMessagesAPIErrorModal({
          code: error.code || -1,
          message: error.message,
          referencedRequestParameter: error.referencedRequestParameter || null,
        });
      } else if (contextErrors && contextErrors.length) {
        const error = contextErrors?.[0];
        setGlobalMessagesAPIErrorModal({
          code: -1,
          message: error.message,
          referencedRequestParameter: null,
        });
      }

      return response;
    }, [setGlobalMessagesAPIErrorModal]);
  },
  useGetStatesForErrorCodeMiddleware() {
    const [errors, setErrors] = React.useState<Error[]>();
    return {
      errors,
      setErrors,
    };
  },
  useErrorCodeMiddleware() {
    const {
      errors,
      setErrors,
    } = apolloMiddlewareHooks.useGetStatesForErrorCodeMiddleware();
    const handleErrorCodeMiddleware: ApolloMiddleware =
      React.useCallback((response) => {
        const errorsData = response.data?.data;
        const errors = typeof errorsData === 'object'
          && errorsData !== null
          && 'errors' in errorsData
          && errorsData.errors;
        if (errors && errors.length) setErrors(errors as Error[]);
        return response;
      }, [setErrors]);

    const resetApiError = React.useCallback(() => {
      setErrors([]);
    }, [setErrors]);

    return {
      errors,
      setErrors,
      handleErrorCodeMiddleware,
      error: errors?.[0],
      resetApiError,
    };
  },
};
